import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import ImageCarousel from '../ImageCarousel';
import type { POI } from '../../types';

interface POIDetailsDialogProps {
  open: boolean;
  poi: POI | null;
  onClose: () => void;
}

function POIDetailsDialog({ open, poi, onClose }: POIDetailsDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableScrollLock
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 4
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography component="span" variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {poi?.name || 'Point of Interest Details'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'grey.500',
            '&:hover': {
              backgroundColor: 'grey.100',
              color: 'grey.700'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {poi && (
          <Box>
            {/* Content Section */}
            <Box sx={{ mb: 3 }}>
              {poi.description && (
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.6,
                    color: 'text.secondary'
                  }}
                >
                  {poi.description}
                </Typography>
              )}

              {poi.address && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <PlaceIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {poi.address}
                  </Typography>
                </Box>
              )}

              {poi.type && (
                <Chip
                  label={poi.type}
                  color="primary"
                  variant="filled"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </Box>

            {/* Image Carousel Section */}
            {(poi.images && poi.images.length > 0) && (
              <Box sx={{ mt: 3 }}>
                <ImageCarousel
                  images={poi.images || []}
                  title={poi.name || 'Point of Interest'}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default POIDetailsDialog;
